/**
 * Settings → Organization → File activity client API (#118 Task B8).
 *
 * One read endpoint (`GET /api/orgs/:id/file-audit`), gated
 * `view_file_audit:read` server-side (owner/admin by default — see
 * `shared/types/src/capabilities.ts`). This client is a thin pass-through:
 * every filter maps 1:1 onto the route's own query params (B7), and the
 * cursor is round-tripped opaquely — the shape is the route's, not ours to
 * interpret.
 */
import { request } from '@papan/api-client'

/** Mirrors `schema.ts`'s `fileAudit.actorKind` — kept as a local literal
 *  union rather than importing `@papan/database/schema` (server-only, pulls
 *  in drizzle/pg) into a browser bundle. */
export type FileAuditActorKind = 'user' | 'script' | 'agent' | 'system' | 'share'

/** Mirrors `schema.ts`'s `fileAudit.eventType`, same reasoning as above. */
export type FileAuditEventType = 'created' | 'updated' | 'moved' | 'deleted' | 'restored'

/** Opaque page cursor — round-tripped from `nextCursor` back into the next
 *  `list()` call's `cursor` filter. Never constructed by hand. */
export interface FileAuditCursor {
  eventAt: string
  id: string
}

export interface FileAuditRow {
  id: string
  tenantId: string
  fileId: string | null
  eventType: FileAuditEventType
  actorKind: FileAuditActorKind
  /** Set only for `actorKind === 'user'` (the Better Auth user UUID —
   *  internal; the UI renders `actorEmail`, not this). */
  actorUserId: string | null
  /** #1263: a share-link visitor's actor id (`share:<link>[:…]`), set only for `actorKind === 'share'`. */
  actorShareId?: string | null
  /** Set only for `actorKind === 'user'` (the acting member's email) —
   *  the hover/tooltip on `ActorCell` and what `?actor=` filters on. */
  actorEmail: string | null
  /** Set only for `actorKind === 'user'` (the acting member's display name)
   *  — what `ActorCell` renders, falling back to the email local-part. */
  actorName: string | null
  actorMemberId: string | null
  path: string | null
  /** Set for `eventType === 'moved'` (rename/move) — the path before. */
  oldPath: string | null
  oldValue: unknown
  newValue: unknown
  /** The connection the write came through — a personal access token or an MCP grant (`connectionId` is the grant
   *  id) — when it did; null for the member's own writes (0319). */
  connectionKind?: 'pat' | 'mcp' | null
  connectionId?: string | null
  connectionName?: string | null
  eventAt: string
}

export interface FileAuditFilter {
  /** Case-insensitive substring over the actor's display name / email / kind
   *  — the UI's "Actor" box (matches what the column shows). */
  actor?: string
  fileId?: string
  /** Case-insensitive substring match on `path` (#580) — the UI's
   *  "filter by file name" box. */
  q?: string
  type?: FileAuditEventType
  /** Inclusive lower bound, ISO timestamp. */
  from?: string
  /** Inclusive upper bound, ISO timestamp. */
  to?: string
  cursor?: FileAuditCursor
  /** Clamped server-side to [1, 200]; defaults to 50. */
  limit?: number
}

export interface FileAuditListResponse {
  rows: FileAuditRow[]
  nextCursor: FileAuditCursor | null
}

export const fileAuditApi = {
  async list(tenantId: string, filters: FileAuditFilter = {}): Promise<FileAuditListResponse> {
    const params = new URLSearchParams()
    if (filters.actor) params.set('actor', filters.actor)
    if (filters.fileId) params.set('fileId', filters.fileId)
    if (filters.q) params.set('q', filters.q)
    if (filters.type) params.set('type', filters.type)
    if (filters.from) params.set('from', filters.from)
    if (filters.to) params.set('to', filters.to)
    if (filters.limit) params.set('limit', String(filters.limit))
    // Round-tripped verbatim — the route's own boundary validates shape
    // (JSON.parse + field checks), so this client never needs to.
    if (filters.cursor) params.set('cursor', JSON.stringify(filters.cursor))
    const qs = params.toString()
    return request<FileAuditListResponse>(`/api/orgs/${encodeURIComponent(tenantId)}/file-audit${qs ? `?${qs}` : ''}`)
  },
}
